import React, { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Alert } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ArrowForward } from "@mui/icons-material";
import { Calendar as CalendarIcon } from "iconsax-react";

// `variant` (2026-08-17, Rahim's explicit ask, "just for here" -- Reports
// only): this popup is shared with 2 Dashboard components
// (ClosedContactCount.jsx/ContactsOverview.jsx) which keep the original
// look untouched (variant defaults to "default", nothing about their
// rendering changes). Reports' 2 callers (ReportsDateRangeControl.jsx/
// BroadcastsReport.jsx) pass variant="reports" to opt into the polished
// styling below instead -- one component, two independent looks, neither
// caller's behavior affected by the other's ask.
const DateLabel = ({ startDateProp, endDateProp, onClose, onDateChange, maxRangeDays = 365, variant = "default" }) => {
  const isReports = variant === "reports";
  const [open, setOpen] = useState(true);
  const [startDate, setStartDate] = useState(dayjs(startDateProp || new Date()));
  const [endDate, setEndDate] = useState(dayjs(endDateProp || new Date()));
  const [dateError, setDateError] = useState('');

  const handleClose = () => {
    setOpen(false);
    onClose(); // Notify the parent component
  };

  const validateDates = (start, end) => {
    if (!start || !end) {
      return 'Please select both start and end dates';
    }
    
    if (start.isAfter(end)) {
      return 'Start date must be before or equal to end date';
    }
    
    // Check if the date range is too large -- caller-configurable
    // (2026-08-15, Reports' "Filter by Count" passes a much tighter 31-day
    // cap; every other existing caller keeps the original 365-day default).
    const daysDiff = end.diff(start, 'days');
    if (daysDiff > maxRangeDays) {
      return `Date range cannot exceed ${maxRangeDays} days`;
    }
    
    return '';
  };

  const handleApply = () => {
    const error = validateDates(startDate, endDate);
    if (error) {
      setDateError(error);
      return;
    }
    
    setDateError('');
    const formattedStartDate = startDate.toISOString();
    const formattedEndDate = endDate.toISOString();
    onDateChange(formattedStartDate, formattedEndDate); // Update parent component
    handleClose();
  };

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    // Clear error when user starts typing
    if (dateError) {
      setDateError('');
    }
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
    // Clear error when user starts typing
    if (dateError) {
      setDateError('');
    }
  };

  // Reports variant: normal grey outline (green only on focus, same as
  // every other input in the app) instead of a thick green border at all
  // times -- was the same weight/color whether idle, hovered, or focused,
  // which read as an error/alert state rather than a normal field.
  const datePickerSx = isReports
    ? {
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
          fontSize: "0.9rem",
          "& fieldset": { borderColor: (theme) => theme.palette.divider },
          "&:hover fieldset": { borderColor: "text.primary" },
          "&.Mui-focused fieldset": { borderColor: "primary.main", borderWidth: "1.5px" },
        },
        "& .MuiInputLabel-root": { fontSize: "0.8rem" },
      }
    : {
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "green",
            borderWidth: "2px",
          },
          "&:hover fieldset": {
            borderColor: "green",
          },
          "&.Mui-focused fieldset": {
            borderColor: "green",
          },
        },
      };
  // Tick icon from iconsax (our standard icon library) for the calendar
  // open-picker button, Reports variant only -- Dashboard's 2 callers keep
  // MUI's default icon, untouched.
  const datePickerSlots = isReports
    ? { openPickerIcon: () => <CalendarIcon size={18} color="currentColor" /> }
    : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: isReports
            ? {
                padding: 1,
                maxWidth: 420,
                borderRadius: "16px",
              }
            : {
                minHeight: 300,
                padding: 2,
                maxWidth: 400,
                borderRadius: "16px",
              },
        }}
      >
        <DialogTitle sx={isReports ? { px: 1, pt: 1, pb: 2 } : undefined}>
          <Typography
            align="center"
            sx={
              isReports
                ? { fontSize: "1.2rem", color: "text.primary", fontWeight: 600 }
                : { fontSize: "1.8rem", color: "green", fontWeight: "bold", marginBottom: "0.5rem" }
            }
          >
            Custom Range
          </Typography>
        </DialogTitle>
        <DialogContent>
          {dateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dateError}
            </Alert>
          )}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: isReports ? 3 : 2, mb: isReports ? 3 : 1 }}
          >
            <Box>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField {...params} />}
                // Prevent selecting start date after end date, AND (when
                // maxRangeDays is tighter than the 365-day default) too far
                // before it -- whichever bound is closer wins, so the
                // invalid combination is never selectable in the first
                // place instead of only erroring after Apply.
                maxDate={endDate || undefined}
                minDate={maxRangeDays < 365 && endDate ? endDate.subtract(maxRangeDays, "day") : undefined}
                sx={datePickerSx}
                slots={datePickerSlots}
              />
            </Box>
            <Box textAlign="center">
              <ArrowForward fontSize="medium" sx={{ mx: 1 }} />
            </Box>
            <Box>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField {...params} />}
                // Same bound, other direction -- end date can't be picked
                // before start date, or so far after it that the range
                // would exceed maxRangeDays.
                minDate={startDate || undefined}
                maxDate={maxRangeDays < 365 && startDate ? startDate.add(maxRangeDays, "day") : undefined}
                sx={datePickerSx}
                slots={datePickerSlots}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "right", p: isReports ? 2 : 1 }}>
          <Button onClick={handleClose} sx={{ color: isReports ? "text.secondary" : "black", mr: 1, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            sx={isReports ? { textTransform: "none", borderRadius: "8px" } : { backgroundColor: "green", color: "white" }}
            variant="contained"
            color={isReports ? "primary" : undefined}
            disabled={!startDate || !endDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default DateLabel;
